import { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";

function EmployeeCallbacks() {
  const navigate = useNavigate();

  const [employee, setEmployee] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const todayDate = new Date()
    .toISOString()
    .split("T")[0];

  useEffect(() => {
    const savedEmployee =
      localStorage.getItem("crmEmployee");

    if (!savedEmployee) {
      navigate("/employee-login");
      return;
    }

    let parsedEmployee;

    try {
      parsedEmployee =
        JSON.parse(savedEmployee);
    } catch (error) {
      localStorage.removeItem(
        "crmEmployee"
      );

      navigate("/employee-login");
      return;
    }

    if (!parsedEmployee?.email) {
      navigate("/employee-login");
      return;
    }

    setEmployee(parsedEmployee);

    const loadCallbacks = async () => {
      try {
        setLoading(true);

        const employeeEmail =
          parsedEmployee.email
            .trim()
            .toLowerCase();

        const callbackQuery = query(
          collection(db, "leads"),
          where(
            "assignedEmployeeEmail",
            "==",
            employeeEmail
          )
        );

        const snapshot =
          await getDocs(callbackQuery);

        const list =
          snapshot.docs.map(
            (documentItem) => ({
              id: documentItem.id,
              ...documentItem.data(),
            })
          );

        setLeads(list);
      } catch (error) {
        console.error(
          "Callback loading error:",
          error
        );
      } finally {
        setLoading(false);
      }
    };

    loadCallbacks();
  }, [navigate]);

  const getTimeValue = (value) => {
    if (!value) {
      return 0;
    }

    try {
      if (
        typeof value.toDate ===
        "function"
      ) {
        return value
          .toDate()
          .getTime();
      }

      const date =
        new Date(value);

      return Number.isNaN(
        date.getTime()
      )
        ? 0
        : date.getTime();
    } catch (error) {
      return 0;
    }
  };

  const getLastWorkedTime = (
    lead
  ) => {
    /*
      IMPORTANT:
      We use lastContactedAt only.

      updatedAt is NOT used because changing
      customer name / phone / admin editing
      should not count as the employee
      working the lead.
    */

    return getTimeValue(
      lead.lastContactedAt
    );
  };

  const callbacks =
    useMemo(() => {
      return leads
        .filter(
          (lead) =>
            lead.callbackStatus ===
              "Pending" &&
            lead.callbackDate ===
              todayDate
        )
        .sort((a, b) => {
          const aWorked =
            getLastWorkedTime(a);

          const bWorked =
            getLastWorkedTime(b);

          /*
            Fresh / untouched customers
            always remain on top.
          */

          if (
            !aWorked &&
            bWorked
          ) {
            return -1;
          }

          if (
            aWorked &&
            !bWorked
          ) {
            return 1;
          }

          /*
            If both are fresh,
            nearest callback time first.
          */

          if (
            !aWorked &&
            !bWorked
          ) {
            return (
              (a.callbackTime || "")
                .localeCompare(
                  b.callbackTime || ""
                )
            );
          }

          /*
            Both have already been worked.

            Older worked customer remains
            above.

            Most recently updated customer
            automatically goes to bottom.
          */

          return (
            aWorked -
            bWorked
          );
        });
    }, [
      leads,
      todayDate,
    ]);

  return (
    <div className="employee-calls-page">

      <header className="mobile-crm-header">

        <div>

          <h1>
            Trading Wealth CRM
          </h1>

          <p>
            Today’s Callbacks
          </p>

        </div>

        <button
          type="button"
          className="mobile-back-button"
          onClick={() =>
            navigate(
              "/employee-calls"
            )
          }
        >
          My Calls
        </button>

      </header>

      <main className="employee-calls-main">

        <section className="employee-calls-heading">

          <p className="small-label">
            TODAY'S CALLBACKS
          </p>

          <h2>
            {employee?.name ||
              "Employee"}
          </h2>

          <p>
            Fresh callbacks stay at the top.
            Once updated, the customer moves
            below the remaining fresh callbacks.
          </p>

        </section>

        {loading ? (

          <div className="employee-empty-state">
            Loading callbacks...
          </div>

        ) : callbacks.length ===
          0 ? (

          <div className="employee-empty-state">
            No callbacks pending for today.
          </div>

        ) : (

          <div className="mobile-lead-list">

            {callbacks.map(
              (lead) => (

                <article
                  key={lead.id}
                  className="mobile-lead-card"
                >

                  <div className="mobile-lead-card-top">

                    <div>

                      <h3>
                        {lead.customerName ||
                          lead.name ||
                          "Customer"}
                      </h3>

                      <p>
                        {lead.phone ||
                          "-"}
                      </p>

                    </div>

                    <span className="mobile-status callback">
                      {lead.callbackTime ||
                        "Callback"}
                    </span>

                  </div>

                  <div className="mobile-lead-meta">

                    <span>
                      Time:{" "}
                      {lead.callbackTime ||
                        "-"}
                    </span>

                    <span>
                      Status:{" "}
                      {lead.status ||
                        "Pending"}
                    </span>

                  </div>

                  {lead.lastRemark && (

                    <p className="latest-remark-preview">
                      Latest:{" "}
                      {lead.lastRemark}
                    </p>

                  )}

                  <div className="mobile-lead-actions">

                    <a
                      className="small-call-button"
                      href={`tel:${
                        lead.phone || ""
                      }`}
                    >
                      Call
                    </a>

                    <button
                      type="button"
                      className="open-lead-button"
                      onClick={() =>
                        navigate(
                          `/employee-calls?lead=${lead.id}`
                        )
                      }
                    >
                      Open & Update
                    </button>

                  </div>

                </article>

              )
            )}

          </div>

        )}

      </main>

    </div>
  );
}

export default EmployeeCallbacks;